import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import api from '../utils/api';
import clsx from 'clsx';

const RejectedComprobantes = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth() || { user: null, loading: true };
  const [comprobantes, setComprobantes] = useState([]);
  const [days, setDays] = useState([]);
  const [matrizadores, setMatrizadores] = useState([]);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedMatrizador, setSelectedMatrizador] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Validar permisos
  useEffect(() => {
    if (!loading && (!user || !user.is_contador)) {
      toast.error('No tienes permisos para acceder a esta página.', {
        position: 'top-right',
        autoClose: 3000,
      });
      navigate('/login');
    }
  }, [user, loading, navigate]);

  // Cargar días disponibles
  useEffect(() => {
    const fetchDays = async () => {
      try {
        const response = await api.get('/api/audit/document-days/');
        const fetchedDays = Array.isArray(response.data.days) ? response.data.days : [];
        setDays(fetchedDays);
        if (fetchedDays.length > 0) {
          const targetDay = fetchedDays.find(day => day === '2025-05-12') || fetchedDays[0];
          setSelectedDay(targetDay);
        }
      } catch (error) {
        toast.error('Error al cargar los días disponibles.', { position: 'top-right', autoClose: 5000 });
      }
    };
    if (!loading && user && user.is_contador) {
      fetchDays();
    }
  }, [user, loading]);

  // Cargar comprobantes rechazados
  const fetchComprobantes = useCallback(async () => {
    if (!selectedDay) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get('/api/audit/comprobantes/', {
        params: { fecha_documento: selectedDay },
      });
      const rejected = response.data.filter(comprobante => comprobante.aprobado === false && comprobante.notes);
      // Filtrar comprobantes para asegurarnos de que solo incluyan trámites del día seleccionado
      const filteredRejected = rejected.filter(comprobante =>
        comprobante.tramites.every(tramite => tramite.fecha_documento === selectedDay)
      );
      setComprobantes(filteredRejected);
      const uniqueMatrizadores = [...new Set(
        filteredRejected.flatMap(comprobante => 
          comprobante.tramites.map(tramite => tramite.usuario_matrizador)
        )
      )].sort();
      setMatrizadores(uniqueMatrizadores);
    } catch (err) {
      setError('Error al cargar los comprobantes rechazados.');
      toast.error('Error al cargar los comprobantes rechazados.', { position: 'top-right', autoClose: 5000 });
    } finally {
      setIsLoading(false);
    }
  }, [selectedDay]);

  useEffect(() => {
    if (selectedDay && user && user.is_contador) {
      fetchComprobantes();
    }
  }, [selectedDay, user, fetchComprobantes]);

  // Filtrar comprobantes por matrizador
  const filteredComprobantes = useMemo(() => {
    if (!selectedMatrizador) return comprobantes;
    return comprobantes.filter(comprobante =>
      comprobante.tramites.some(tramite => tramite.usuario_matrizador === selectedMatrizador)
    );
  }, [comprobantes, selectedMatrizador]);

  // Manejo de cambio de día
  const handleDayChange = (e) => {
    setSelectedDay(e.target.value);
    setSelectedMatrizador('');
    setComprobantes([]);
    setMatrizadores([]);
  };

  // Manejo de cambio de matrizador
  const handleMatrizadorChange = (e) => {
    setSelectedMatrizador(e.target.value);
  };

  if (loading) return <div className="text-white text-center">Cargando...</div>;
  if (!user) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="min-h-screen flex flex-col text-white p-4 sm:p-6 bg-gray-900"
    >
      <header className="flex justify-between items-center mb-6 sm:mb-8">
        <div className="flex items-center">
          <img src={logo} alt="Logo Notaría 4 Cantón Otavalo" className="w-12 h-auto mr-3 sm:w-16 sm:mr-4" />
          <h1 className="text-2xl sm:text-3xl font-bold">Comprobantes Rechazados</h1>
        </div>
        <Link
          to="/dashboard/contador"
          className="bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-4 rounded-full hover:bg-notaria-red hover:text-white transition-all"
        >
          Volver al Dashboard
        </Link>
      </header>

      <main className="flex-1">
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-7xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-4 sm:p-6 rounded-xl shadow-2xl mb-6 sm:mb-8"
        >
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">Filtros</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label htmlFor="fecha_documento" className="block text-sm sm:text-lg font-semibold mb-2">
                Día de los Trámites
              </label>
              <select
                id="fecha_documento"
                value={selectedDay}
                onChange={handleDayChange}
                className="w-full p-2 sm:p-3 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
              >
                <option value="">Seleccione un día</option>
                {days.map((day) => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="usuario_matrizador" className="block text-sm sm:text-lg font-semibold mb-2">
                Seleccionar Matrizador
              </label>
              <select
                id="usuario_matrizador"
                value={selectedMatrizador}
                onChange={handleMatrizadorChange}
                className="w-full p-2 sm:p-3 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
              >
                <option value="">Todos los Matrizadores</option>
                {matrizadores.map((matrizador) => (
                  <option key={matrizador} value={matrizador}>{matrizador}</option>
                ))}
              </select>
            </div>
          </div>
        </motion.div>

        {isLoading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-lg text-gray-200"
          >
            Cargando comprobantes rechazados...
          </motion.div>
        ) : error ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-7xl mx-auto bg-red-500 text-white p-4 rounded-lg mb-6 sm:mb-8"
          >
            {error}
          </motion.div>
        ) : !selectedDay ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-lg text-gray-400"
          >
            Por favor, selecciona un día para ver los comprobantes rechazados.
          </motion.div>
        ) : (
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-7xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-4 sm:p-6 rounded-xl shadow-2xl"
          >
            <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">Comprobantes Rechazados</h2>
            {filteredComprobantes.length === 0 ? (
              <p className="text-center text-lg text-gray-400">No hay comprobantes rechazados para los filtros seleccionados.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-notaria-blue-2 text-white">
                      <th className="p-3 text-left">Comprobante #</th>
                      <th className="p-3 text-left">Tipo de Pago</th>
                      <th className="p-3 text-left">No. Transacción</th>
                      <th className="p-3 text-left">Fecha Depósito</th>
                      <th className="p-3 text-left">Fecha Documento</th>
                      <th className="p-3 text-left">Monto</th>
                      <th className="p-3 text-left">Motivo del Rechazo</th>
                      <th className="p-3 text-left">Matrizador</th>
                      <th className="p-3 text-left">Creado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredComprobantes.map((comprobante) => (
                      <motion.tr
                        key={comprobante.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="border-b border-notaria-blue-2 hover:bg-notaria-blue-3"
                      >
                        <td className="p-3">{comprobante.id}</td>
                        <td className="p-3">{comprobante.tipo_pago}</td>
                        <td className="p-3">{comprobante.numero_transaccion}</td>
                        <td className="p-3">{comprobante.fecha_deposito}</td>
                        <td className="p-3">
                          {[...new Set(comprobante.tramites.map(tramite => tramite.fecha_documento))].join(', ')}
                        </td>
                        <td className="p-3">${parseFloat(comprobante.monto_total || 0).toFixed(2)}</td>
                        <td className="p-3">{comprobante.notes || 'No especificado'}</td>
                        <td className="p-3">
                          {[...new Set(comprobante.tramites.map(tramite => tramite.usuario_matrizador))].join(', ')}
                        </td>
                        <td className="p-3">{new Date(comprobante.created_at).toLocaleString()}</td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        )}
      </main>

      <ToastContainer position="top-right" autoClose={3000} pauseOnHover />
    </motion.div>
  );
};

export default RejectedComprobantes;